/**
 * TabsDisabledState — the friendly placeholder shown on Dashboard / Nodes /
 * Pulse Groups when the runtime "Enable Tabs" gate is OFF.
 *
 * Role-aware (via useAuth):
 *   - admin   → "Enable now" (flips the flag) + a link to Lifecycle.
 *   - others  → "Notify admins" (emails all admins, globally rate-limited to
 *               one per 5 min); the button shows a live countdown afterwards.
 *
 * Animation honors prefers-reduced-motion (static illustration).
 *
 * Sidebar Roles + Enable-Tabs Gate (2026-06-07).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Power, Mail, ArrowRight, Clock, CheckCircle2 } from 'lucide-react';
import { Button, Card, CardContent, useToast } from '@/components/common';
import { useAuth } from '@/contexts/AuthContext';
import { useRolePath } from '@/lib/rolePath';
import { appSettingsApi } from '@/api/client';

interface TabsDisabledStateProps {
  /** The tab being viewed, e.g. "Dashboard" — personalizes the copy. */
  pageLabel?: string;
  /** Called after the admin enables the tabs so the gate can re-render. */
  onEnabled?: () => void;
}

function fmtCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** A calm "cluster at rest" illustration: a hub with slow pulse rings, a few
 *  gently bobbing satellite nodes, and drifting sparkles. */
function RestingClusterSvg() {
  const reduce = useReducedMotion();
  const satellites = [
    { x: 96, y: 78, r: 7, c: '#2563eb', delay: 0 },
    { x: 372, y: 96, r: 6, c: '#0891b2', delay: 0.4 },
    { x: 120, y: 196, r: 6, c: '#7c3aed', delay: 0.8 },
    { x: 360, y: 198, r: 7, c: '#0F6CBD', delay: 1.2 },
  ];
  const sparkles = [
    { x: 200, y: 60, delay: 0 },
    { x: 270, y: 86, delay: 0.6 },
    { x: 232, y: 44, delay: 1.1 },
  ];
  return (
    <svg viewBox="0 0 480 260" className="mx-auto w-full max-w-md" role="img" aria-label="The cluster is resting">
      <defs>
        <radialGradient id="tds-hub" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#0F6CBD" />
        </radialGradient>
        <linearGradient id="tds-horizon" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* warm horizon glow — good vibes */}
      <ellipse cx="240" cy="232" rx="150" ry="34" fill="url(#tds-horizon)" />

      {/* calm pulse rings */}
      {[0, 1, 2].map((i) =>
        reduce ? (
          <circle key={i} cx="240" cy="140" r={44 + i * 22} fill="none" stroke="#bfdbfe" strokeWidth="1.5" opacity={0.5 - i * 0.13} />
        ) : (
          <motion.circle
            key={i}
            cx="240"
            cy="140"
            fill="none"
            stroke="#60a5fa"
            strokeWidth="1.5"
            initial={{ r: 38, opacity: 0.5 }}
            animate={{ r: [38, 96], opacity: [0.5, 0] }}
            transition={{ duration: 4, repeat: Infinity, delay: i * 1.3, ease: 'easeOut' }}
          />
        )
      )}

      {/* satellite nodes, gently bobbing */}
      {satellites.map((n, i) => {
        const dot = <circle cx={n.x} cy={n.y} r={n.r} fill={n.c} />;
        const line = <line x1="240" y1="140" x2={n.x} y2={n.y} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 4" />;
        return (
          <g key={i}>
            {line}
            {reduce ? dot : (
              <motion.g
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3.4, repeat: Infinity, delay: n.delay, ease: 'easeInOut' }}
              >
                {dot}
              </motion.g>
            )}
          </g>
        );
      })}

      {/* central hub */}
      <circle cx="240" cy="140" r="28" fill="url(#tds-hub)" />
      <circle cx="240" cy="140" r="28" fill="none" stroke="#ffffff" strokeOpacity="0.6" strokeWidth="2" />
      {/* "resting" zzz */}
      <text x="262" y="118" fontSize="15" fontWeight="700" fill="#0F6CBD" opacity="0.8">z</text>
      <text x="274" y="108" fontSize="11" fontWeight="700" fill="#0F6CBD" opacity="0.6">z</text>

      {/* drifting sparkles */}
      {sparkles.map((s, i) =>
        reduce ? (
          <circle key={i} cx={s.x} cy={s.y} r="2" fill="#93c5fd" />
        ) : (
          <motion.circle
            key={i}
            cx={s.x}
            cy={s.y}
            r="2"
            fill="#93c5fd"
            animate={{ opacity: [0, 1, 0], y: [s.y, s.y - 12] }}
            transition={{ duration: 2.6, repeat: Infinity, delay: s.delay, ease: 'easeInOut' }}
          />
        )
      )}
    </svg>
  );
}

export default function TabsDisabledState({ pageLabel, onEnabled }: TabsDisabledStateProps) {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const { showToast } = useToast();
  const rp = useRolePath();

  const [enabling, setEnabling] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [requested, setRequested] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);

  // Countdown ticker for the "Notify admins" cooldown.
  useEffect(() => {
    if (cooldown <= 0) return;
    tick.current = setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => {
      if (tick.current) clearInterval(tick.current);
    };
  }, [cooldown > 0]);

  const handleEnable = useCallback(async () => {
    setEnabling(true);
    try {
      await appSettingsApi.setTabsEnabled(true);
      showToast('Tabs enabled — loading data…', 'success');
      onEnabled?.();
    } catch {
      showToast('Could not enable tabs. Please try again.', 'error');
    } finally {
      setEnabling(false);
    }
  }, [showToast, onEnabled]);

  const handleNotify = useCallback(async () => {
    setNotifying(true);
    try {
      const res = await appSettingsApi.requestEnable();
      setRequested(true);
      setCooldown(res.cooldown_remaining_seconds || 300);
      showToast('Your request was sent to the admins.', 'success');
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { detail?: { cooldown_remaining_seconds?: number; message?: string } } } };
      if (e.response?.status === 429) {
        const remaining = e.response.data?.detail?.cooldown_remaining_seconds ?? 300;
        setRequested(true);
        setCooldown(remaining);
        showToast('An admin was already notified recently. Please wait a few minutes.', 'info');
      } else {
        showToast('Could not send the request. Please try again.', 'error');
      }
    } finally {
      setNotifying(false);
    }
  }, [showToast]);

  const heading = useMemo(() => {
    if (isAdmin) return 'The data tabs are switched off';
    return 'The cluster is resting';
  }, [isAdmin]);

  const subtext = useMemo(() => {
    const where = pageLabel ? `${pageLabel} and the other live dashboards` : 'The live dashboards';
    if (isAdmin) {
      return `${where} are paused to save resources. Enable them to start loading data.`;
    }
    return `${where} switch on once an admin starts the cluster. Want them now? Let the admins know and they'll get you set up.`;
  }, [isAdmin, pageLabel]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <Card variant="glow">
        <CardContent className="p-8 text-center">
          <RestingClusterSvg />
          <h2 className="mt-4 text-xl font-bold text-gray-900">{heading}</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-500">{subtext}</p>

          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {isAdmin ? (
              <>
                <Button variant="primary" size="lg" isLoading={enabling} leftIcon={<Power size={18} />} onClick={handleEnable}>
                  Enable now
                </Button>
                <Link to={rp('/lifecycle')}>
                  <Button variant="outline" size="lg" rightIcon={<ArrowRight size={18} />}>
                    Go to Lifecycle
                  </Button>
                </Link>
              </>
            ) : cooldown > 0 ? (
              <Button variant="outline" size="lg" disabled leftIcon={<Clock size={18} />}>
                Admins notified · retry in {fmtCountdown(cooldown)}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="lg"
                isLoading={notifying}
                leftIcon={requested ? <CheckCircle2 size={18} /> : <Mail size={18} />}
                onClick={handleNotify}
              >
                Notify admins
              </Button>
            )}
          </div>

          {!isAdmin && requested && cooldown > 0 && (
            <p className="mt-3 text-xs text-gray-400">
              We won't send another email for a few minutes to avoid spamming your admins.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
